import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, ArrowRight, CheckCircle2, Search } from "lucide-react";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { canonical } from "@/lib/seo";
import { CostMidCta } from "@/components/site/CostMidCta";
import { InstantEstimateStrip } from "@/components/site/InstantEstimateStrip";

const TITLE = "Phoenix Roofing Costs: Foam, Tile, Repairs & Roof Recoating";
const DESCRIPTION =
  "Realistic Phoenix roofing cost guidance for foam roofs, recoating, tile replacement, leak repair, skylights, and commercial roofing. Schedule a free on-site inspection from Durafoam Roofing.";

const COST_CARDS: { title: string; href: string; blurb: string }[] = [
  {
    title: "Foam Roof Recoating Cost",
    href: "/foam-roof-recoating-cost-in-phoenix",
    blurb:
      "If your foam roof is still structurally sound, a recoat extends its life for years at a fraction of replacement cost.",
  },
  {
    title: "Foam Roof Replacement Cost",
    href: "/foam-roof-replacement-cost-phoenix",
    blurb:
      "Full tear-off and re-spray pricing for foam roofs that are past the point of a recoat.",
  },
  {
    title: "Tile Roof Replacement / R&R Cost",
    href: "/tile-roof-replacement-cost-in-phoenix",
    blurb:
      "Remove-and-replace pricing for failed underlayment under existing tile — the most common tile roof project in Phoenix.",
  },
  {
    title: "Roof Repair Cost",
    href: "/roof-repair-cost-phoenix",
    blurb:
      "Typical repair ranges for foam, tile, and shingle roofs across the Phoenix Valley.",
  },
  {
    title: "Flat Roof Repair Cost",
    href: "/flat-roof-repair-cost-phoenix",
    blurb:
      "What flat roof leaks, ponding water, and coating failures typically cost to fix.",
  },
  {
    title: "Commercial Roofing Cost",
    href: "/commercial-roofing-cost-phoenix",
    blurb:
      "Pricing factors for commercial foam, coating, and flat-roof projects in the Valley.",
  },
  {
    title: "Skylight Repair Cost",
    href: "/skylight-repair-cost-phoenix",
    blurb:
      "Reseal, flash, or replace — typical Phoenix skylight repair pricing and what drives it.",
  },
  {
    title: "Shingle Roof Cost",
    href: "/shingle-roof-cost-phoenix",
    blurb:
      "Asphalt shingle replacement pricing for Phoenix homes and pitched roof projects.",
  },
];

const INSPECTION_REASONS = [
  {
    title: "Foam may only need recoating",
    body: "A healthy foam substrate can often be recoated instead of replaced — saving thousands.",
  },
  {
    title: "Tile roofs hide underlayment issues",
    body: "The tile usually looks fine. The underlayment underneath is what fails and drives the real cost.",
  },
  {
    title: "Leaks come from many sources",
    body: "Drains, scuppers, skylights, penetrations, and roof-to-wall transitions all need to be ruled out before pricing a repair.",
  },
  {
    title: "Ponding water changes the plan",
    body: "Standing water can turn a small repair into a slope or drainage correction. We confirm on the roof, not by phone.",
  },
  {
    title: "Photos and on-site notes prevent bad estimates",
    body: "We document conditions so the written assessment matches what's actually on your roof.",
  },
];

function serviceSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Phoenix Roofing Cost Guide",
    provider: {
      "@type": "RoofingContractor",
      name: "Durafoam Roofing",
      telephone: "+1-480-941-5373",
      areaServed: "Phoenix, AZ",
    },
    areaServed: "Phoenix, AZ",
    url: canonical("/roofing-costs-phoenix"),
  };
}

export const Route = createFileRoute("/roofing-costs-phoenix")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonical("/roofing-costs-phoenix") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: canonical("/roofing-costs-phoenix") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(serviceSchema()),
      },
    ],
  }),
  component: RoofingCostsPhoenix,
});

function RoofingCostsPhoenix() {
  return (
    <>
      <InstantEstimateStrip />
      {/* Hero */}
      <section className="relative bg-primary text-white">
        <Container className="py-16 lg:py-24">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
            Phoenix Roofing Costs
          </p>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-[1.15] text-white sm:text-4xl lg:text-5xl">
            How Much Does Roofing Cost in Phoenix?
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/90 sm:text-lg">
            Get realistic cost guidance for foam roofing, roof recoating, tile roof replacement,
            leak repair, and commercial roofing in the Phoenix area. Every roof is different,
            so the best next step is a free on-site roof inspection from Durafoam Roofing.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              <Search className="h-4 w-4" aria-hidden="true" />
              Schedule Free Roof Inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>

      {/* Cost Cards */}
      <section className="bg-background py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-[26px] font-semibold lg:text-4xl">
              Phoenix roofing cost guides by project type
            </h2>
            <p className="mt-4 text-[var(--muted-foreground)]">
              Each guide breaks down typical pricing, what drives the cost, and how to save money.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {COST_CARDS.map((c) => (
              <div
                key={c.href}
                className="flex flex-col rounded-xl border-[1.5px] border-border bg-white p-6 transition-colors hover:border-accent"
              >
                <h3 className="text-[20px] font-semibold text-primary">{c.title}</h3>
                <p className="mt-2 flex-1 text-[15px] leading-relaxed text-foreground/80">
                  {c.blurb}
                </p>
                <div className="mt-5">
                  <Link
                    to={c.href}
                    className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-primary hover:text-accent"
                  >
                    View Cost Guide <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Cost Range Guidance */}
      <section className="bg-[var(--surface)] py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl">
            <h2 className="text-[26px] font-semibold text-primary lg:text-4xl">
              What Phoenix roofing actually costs
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-foreground/85">
              Typical Phoenix-area pricing can vary widely depending on roof condition, access,
              size, materials, and whether repair, recoat, or replacement is needed. Two homes on
              the same street can have very different roof costs based on what's actually on the
              roof.
            </p>
            <p className="mt-4 text-[16px] leading-relaxed text-foreground/85">
              Cost is influenced by roof type (foam, tile, shingle, flat), square footage, existing
              damage, ponding water, coating condition, tile underlayment, penetrations, skylights,
              and how easy the roof is to access. We avoid quoting exact prices sight-unseen
              because guesses lead to bad estimates — and homeowners deserve better.
            </p>
            <p className="mt-4 text-[16px] leading-relaxed text-foreground/85">
              Use the cost guides above for honest ranges, then schedule a free on-site inspection
              for a written assessment specific to your roof.
            </p>
          </div>
        </Container>
      </section>

      {/* Why a Free Inspection */}
      <section className="bg-background py-16 lg:py-20">
        <Container>
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-[26px] font-semibold text-primary lg:text-4xl">
              Why We Recommend a Free Roof Inspection Before Giving a Final Price
            </h2>
            <p className="mt-4 text-[var(--muted-foreground)]">
              A 30-minute on-site inspection is the difference between a guess and a real plan.
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2">
            {INSPECTION_REASONS.map((r) => (
              <div key={r.title} className="rounded-xl border border-border bg-white p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
                  <div>
                    <h3 className="text-[17px] font-semibold text-primary">{r.title}</h3>
                    <p className="mt-1.5 text-[15px] leading-relaxed text-foreground/80">{r.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
              <Search className="h-4 w-4" aria-hidden="true" />
              Schedule Free Roof Inspection
            </ButtonLink>
            <ButtonLink href="tel:+14809415373" variant="ghost-light" size="lg">
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 480-941-5373
            </ButtonLink>
          </div>
        </Container>
      </section>

      {/* Mid-page CTA: free inspection */}
      <CostMidCta />
    </>
  );
}
